import JSZip from 'jszip';
import { invoicesService } from '@/services/invoices.service';
import type { Invoice } from '@/types';

export interface ExportProgress {
  /** Number of invoices processed so far (success or failure). */
  completed: number;
  /** Total number of invoices to process. */
  total: number;
  /** Invoice number currently being processed, if any. */
  current?: string;
}

export interface ExportResult {
  /** Invoice numbers successfully added to the archive. */
  succeeded: string[];
  /** Invoices that could not be exported, with the reason. */
  failed: { invoiceNumber: string; reason: string }[];
}

/** Number of PDFs generated/fetched in parallel to avoid hammering the API. */
const CONCURRENCY = 4;

/** Build a safe, unique filename for an invoice PDF inside the archive. */
function pdfFileName(invoice: Invoice, usedNames: Set<string>): string {
  const base = (invoice.invoiceNumber || invoice.id).replace(/[^a-zA-Z0-9._-]+/g, '-');
  let name = `${base}.pdf`;
  let counter = 1;
  while (usedNames.has(name)) {
    name = `${base}-${counter++}.pdf`;
  }
  usedNames.add(name);
  return name;
}

/** Generate a PDF for a single invoice and return its bytes. */
async function fetchInvoicePdf(invoice: Invoice): Promise<ArrayBuffer> {
  const { pdfUrl } = await invoicesService.generateInvoicePdf(invoice.id);
  const response = await fetch(pdfUrl);
  if (!response.ok) {
    throw new Error(`Failed to download PDF (${response.status})`);
  }
  return await response.arrayBuffer();
}

/**
 * Generate PDFs for the given invoices, bundle them into a ZIP archive, and
 * trigger a browser download. PDFs are generated server-side via the existing
 * per-invoice endpoint, fetched as blobs, and zipped client-side.
 */
export async function exportInvoicesToZip(
  invoices: Invoice[],
  options?: {
    fileName?: string;
    onProgress?: (progress: ExportProgress) => void;
  }
): Promise<ExportResult> {
  const zip = new JSZip();
  const usedNames = new Set<string>();
  const result: ExportResult = { succeeded: [], failed: [] };

  const total = invoices.length;
  let completed = 0;

  // Process invoices in batches to bound concurrency.
  for (let i = 0; i < invoices.length; i += CONCURRENCY) {
    const batch = invoices.slice(i, i + CONCURRENCY);
    await Promise.all(
      batch.map(async (invoice) => {
        options?.onProgress?.({ completed, total, current: invoice.invoiceNumber });
        try {
          const bytes = await fetchInvoicePdf(invoice);
          zip.file(pdfFileName(invoice, usedNames), bytes);
          result.succeeded.push(invoice.invoiceNumber);
        } catch (err: any) {
          result.failed.push({
            invoiceNumber: invoice.invoiceNumber,
            reason: err?.message || 'Unknown error',
          });
        } finally {
          completed += 1;
          options?.onProgress?.({ completed, total, current: invoice.invoiceNumber });
        }
      })
    );
  }

  if (result.succeeded.length === 0) {
    return result;
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = options?.fileName || 'invoices.zip';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);

  return result;
}
